import { Reveal } from "@/components/pdp/reveal";
import { useProduct } from "@/components/pdp/PdpDataContext";
import { PlayerChrome } from "@/components/pdp/PlayerChrome";
import { ArrowRight } from "lucide-react";

export const StoryDermatology = ({ cta }) => {
  const P = useProduct();
  const { storyB } = P;
  const isV31 = !!P.v31;
  return (
    <section className="bg-riversol-paper py-20 lg:py-32" data-testid="story-dermatology">
      <div className="max-w-[1500px] mx-auto px-4 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className={`lg:col-span-6 order-2 ${storyB.imageRight ? "lg:order-1" : "lg:order-2"}`}>
          <div className="pdp-eyebrow font-mono text-[11px] tracking-[0.24em] uppercase text-riversol-blue mb-6">
            {storyB.eyebrow}
          </div>
          <h2
            className={
              P.v31
                ? "text-2xl md:text-3xl lg:text-[2rem] leading-[1.2] tracking-tight font-serif italic font-normal text-neutral-900 mb-8 max-w-xl"
                : "text-3xl md:text-4xl lg:text-[3rem] leading-[1.06] tracking-tight font-semibold text-neutral-900 mb-8"
            }
          >
            {storyB.titleLines.map((l, i) => <span key={i} className="block font-sans">{l}</span>)}
          </h2>
          <Reveal>
            <p className="text-[16px] lg:text-[17px] leading-[1.75] lg:leading-[1.8] text-neutral-600 max-w-xl mb-6">
              {storyB.p1}
            </p>
            <p className="text-[16px] lg:text-[17px] leading-[1.75] lg:leading-[1.8] text-neutral-600 max-w-xl">
              {storyB.p2}
            </p>
          </Reveal>

          {cta && (
            <a
              href={cta.href}
              data-testid="dermo-cta"
              className="mt-8 inline-flex items-center gap-2 bg-riversol-blue text-white px-8 py-3.5 text-[12px] tracking-[0.14em] uppercase font-medium hover:bg-riversol-blueLight transition-colors"
            >
              {cta.label}
              <ArrowRight size={15} strokeWidth={1.8} />
            </a>
          )}

          {/* V3.1: Beta-T signature-antioxidant card lives here in the copy
              column so the image can stay a clean square. V2/V3 still show
              it as an overlaid pill inside the image (below). */}
          {isV31 && !P.v32 && (
            <div
              className="mt-8 lg:mt-10 bg-riversol-blue text-white p-6 max-w-md"
              data-testid="dermo-beta-t-card"
            >
              <div className="font-mono text-3xl md:text-4xl mb-2">{storyB.badgeBig}</div>
              <div className="text-sm text-white/75 leading-relaxed">{storyB.badgeText}</div>
            </div>
          )}

          <div className="mt-8 lg:mt-10 pt-6 lg:pt-8 border-t border-neutral-200 flex items-center gap-6">
            {!P.v32 && (
              <div className="w-14 h-14 rounded-full bg-riversol-stone overflow-hidden shrink-0 flex items-center justify-center">
                <span className="font-mono text-xs text-neutral-400 text-center leading-tight px-1 whitespace-pre-line">{storyB.personInitials}</span>
              </div>
            )}
            <div>
              <div className="text-sm font-medium text-neutral-900">{storyB.personName}</div>
              <div className="text-xs text-neutral-500 font-mono tracking-wide">{storyB.personRole}</div>
            </div>
          </div>
        </div>

        <div className={`lg:col-span-6 relative order-1 ${storyB.imageRight ? "lg:order-2" : "lg:order-1"}`}>
          <div
            className={`editorial-frame bg-white group relative overflow-hidden ${
              isV31 ? "aspect-square" : "aspect-[5/6]"
            }`}
            data-testid="dermo-media"
            style={{ clipPath: `inset(0 round ${P.mediaRadius || "15px"})` }}
          >
            <img src={storyB.image} alt={storyB.imageAlt} className="w-full h-full object-cover img-zoom" />
            {isV31 && <PlayerChrome time="0:00 / 0:18" progress={0} />}
          </div>
          {/* V2/V3: overlaid Beta-T badge over the image corner. Hidden on V3.1. */}
          {!isV31 && (
            <div className="absolute top-8 -left-6 bg-riversol-blue text-white p-6 w-52 hidden xl:block">
              <div className="font-mono text-4xl mb-2">{storyB.badgeBig}</div>
              <div className="text-xs text-white/70 leading-relaxed">{storyB.badgeText}</div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default StoryDermatology;
